import { LoadingButton } from "@mui/lab";
import { Box, Button, Link, Typography } from "@mui/material";
import { uploadBatteryModulePhoto } from "actions/ocr";
import BarcodeScanner from "components/BarcodeScanner";
import Modal from "components/Modal";
import { FormField } from "empower-energy-owner-components";
import { isEmpty } from "lodash";
import { ButtonContainer } from "pages/Home/Wizard/components";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import CameraCaptureModal from "components/Modal/CameraCaptureModal";
import { dataURLtoFile } from "utils/wizard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const SERIAL_LENGTH = 24;

const BatteryModuleScannerModal = ({
  selectedSerial,
  open,
  onClose,
  onSave,
}) => {
  const dispatch = useDispatch();
  const [serial, setSerial] = useState("");
  const [errors, setErrors] = useState({ serial: "" });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [serialOcrStatus, setSerialOcrStatus] = useState(""); // '', 'success', 'other-errors'

  // Grab loading status from redux
  const { batteryModulePhotoLoading } = useSelector((state) => state.ocr);

  // Prefill serial value from selectedSerial
  useEffect(() => {
    setSerial(selectedSerial?.value ?? "");
    setErrors({ serial: "" });
    setSerialOcrStatus("");
  }, [selectedSerial, open]);

  // Centralized error checking for serial
  const checkErrors = useCallback(
    (value) => {
      if (isEmpty(value)) {
        return { serial: "Required" };
      } else if (value.length !== SERIAL_LENGTH) {
        return { serial: `Serial should be ${SERIAL_LENGTH} characters.` };
      }
      return { serial: "" };
    },
    []
  );

  // Handler for serial field change
  const handleSerialChange = (value) => {
    setSerial(value);
    setErrors(checkErrors(value));
    setSerialOcrStatus(""); // Reset when manually editing
  };

  // Handler for serial input blur
  const handleBlur = () => {
    setErrors(checkErrors(serial));
  };

  // Save logic, allow only if no validation errors
  const handleSaveChanges = () => {
    const errorObj = checkErrors(serial);
    setErrors(errorObj);
    if (!errorObj.serial) {
      onSave({
        target: {
          name: selectedSerial.name,
          value: serial,
        },
      });
      onClose();
    }
  };

  // Handle OCR from camera modal
  const handleCameraChange = async (img) => {
    setCapturedImage(img);
    setErrors({ serial: "" });
    const file = dataURLtoFile(img, "label.jpg");
    const batteryModulePhotoResponse = await dispatch(uploadBatteryModulePhoto(file));

    if (!batteryModulePhotoResponse) {
      setSerial("");
      setErrors({ serial: "Failed to extract serial, please try again." });
      setSerialOcrStatus("other-errors");
      return;
    }

    switch (batteryModulePhotoResponse.status) {
      case "check_input":
        setSerial(batteryModulePhotoResponse.serial);
        setErrors({ serial: "Check input serial number" });
        setSerialOcrStatus("other-errors");
        break;
      case "extraction_failed":
        setSerial("");
        setErrors({ serial: "Validation failed" });
        setSerialOcrStatus("other-errors");
        break;
      case "success":
        setSerial(batteryModulePhotoResponse.serial);
        setErrors({ serial: "" });
        setSerialOcrStatus("success");
        break;
      default:
        setSerial("");
        setErrors({ serial: "Unknown response" });
        setSerialOcrStatus("other-errors");
        break;
    }
  };

  return (
    <>
      <Modal
        width={600}
        top="10%"
        title={`Battery Module ${selectedSerial.name}`}
        open={open}
        onClose={onClose}
        showHeaderBorder
      >
        <Box>
          <FormField
            label="Enter Serial Number"
            name="serial"
            error={errors.serial}
            value={serial}
            variant="filled"
            onBlur={handleBlur}
            onChange={(e) => handleSerialChange(e.target.value)}
            autoComplete="off"
          />

          {/* Success icon and label below the field if captured from photo and successful */}
          {serialOcrStatus === "success" && (
            <Box display="flex" alignItems="center" mt={1} mb={0.5}>
              <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="success.main" fontWeight={500}>Serial capture success</Typography>
            </Box>
          )}

          <Typography my={2} variant="h4">
            OR Scan Barcode
          </Typography>

          <BarcodeScanner onChange={handleSerialChange} />

          <Box my={1}>
            <Typography variant="body2">
              Note: Some barcodes are difficult to scan. If unable to scan serial using barcode,{" "}
              <Link
                component="button"
                underline="hover"
                onClick={() => setCameraOpen(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  width: "fit-content",
                  fontWeight: 500,
                  my: 2
                }}
              >
                Click here to try taking a photo of the serial number instead.
              </Link>
            </Typography>
          </Box>

          <ButtonContainer>
            <LoadingButton
              onClick={handleSaveChanges}
              variant="contained"
              sx={{ mr: 2 }}
              disabled={isEmpty(serial) || Boolean(errors.serial)}
              loading={batteryModulePhotoLoading}
            >
              Save Changes
            </LoadingButton>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
          </ButtonContainer>

          {cameraOpen && (
            <CameraCaptureModal
              open={cameraOpen}
              onClose={() => setCameraOpen(false)}
              image={capturedImage}
              onChange={handleCameraChange}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default BatteryModuleScannerModal;
